import {
  applyOperations,
  type ConnectionOperation,
  connectionPathKey,
  OPTIMISTIC_KEY,
  type PendingConnectionOperation,
} from '@github-ui/optimistic-connection'

import {TIMELINE_CONNECTION_PATH, type TimelineIssue, type TimelineItemsConnection} from './fetch-timeline-items'
import {identityKeys} from './timeline-assembly'

/**
 * What a write still in flight does to the rows and to the count, as plain functions over a
 * section's result: no React, no cache, nothing about which section an operation reaches. The hook
 * decides that and calls in here to apply it.
 */

const TOTAL_COUNT_PATH_KEY = connectionPathKey([...TIMELINE_CONNECTION_PATH, 'totalCount'])

export const overlaid = <T>(value: T, operations: PendingConnectionOperation[]) =>
  operations.length === 0 ? value : applyOperations(value, operations)

/** What an operation says the timeline's own count should move by. */
export const ownCountDelta = (operation: ConnectionOperation) => {
  if (operation.type !== 'addToConnection' && operation.type !== 'removeFromConnection') return 0
  return operation.counts?.find(count => connectionPathKey(count.path) === TOTAL_COUNT_PATH_KEY)?.delta ?? 0
}

export const readsInclude = (reads: Array<TimelineIssue | undefined>, nodeId: string) =>
  reads.some(read => (read?.timelineItems?.edges ?? []).some(edge => edge?.node?.id === nodeId))

/** Whether a read has come back with the row an add is still pending for. */
export const readsIncludeAdd = (reads: Array<TimelineIssue | undefined>, operation: ConnectionOperation) => {
  const wanted = new Set(identityKeys('node' in operation ? operation.node : undefined))
  if (wanted.size === 0) return false
  return reads.some(read =>
    (read?.timelineItems?.edges ?? []).some(edge => identityKeys(edge?.node).some(identity => wanted.has(identity))),
  )
}

/**
 * The net change to the timeline's own count carried by the operations still in flight.
 *
 * `reads` are the server's own rows, since the overlay is applied on read. So a removal whose row
 * has already gone from them is one a read has counted, and counting the pending delta as well
 * would count it twice.
 */
export const pendingCountDelta = (operations: PendingConnectionOperation[], reads: Array<TimelineIssue | undefined>) =>
  operations.reduce((sum, {operation}) => {
    const delta = ownCountDelta(operation)
    if (delta === 0) return sum
    if (operation.type === 'removeFromConnection' && !readsInclude(reads, operation.nodeId)) return sum
    return sum + delta
  }, 0)

export const withCountDelta = (issue: TimelineIssue, delta: number): TimelineIssue => {
  const connection = issue.timelineItems
  if (!connection || connection.totalCount == null) return issue
  return {...issue, timelineItems: {...connection, totalCount: Math.max(connection.totalCount + delta, 0)}}
}

/**
 * The correlation key a settled write left on a row, by the server id the row came back as.
 * `applyOperations` tags the node it writes into the cache, but only there: every later read returns
 * the row untagged, and the reads are of several windows, so the copy that ends up rendered may be
 * from a window that never saw the write at all.
 */
export const correlationKeysOf = (operation: ConnectionOperation): Array<[string, string]> => {
  if (operation.type !== 'addToConnection') return []
  const {[OPTIMISTIC_KEY]: key, id} = (operation.node ?? {}) as {[OPTIMISTIC_KEY]?: unknown; id?: unknown}
  const named = typeof key === 'string' && typeof id === 'string' && key !== id
  return named ? [[id, key]] : []
}

/**
 * Restores those keys, so a row keeps the React key it was first rendered under
 * (`timelineItemKey`) rather than remounting -- and discarding an edit opened on it -- when a read
 * lands seconds after the write.
 */
export const withCorrelationKeys = (
  connection: TimelineItemsConnection | undefined,
  keys: ReadonlyMap<string, string>,
): TimelineItemsConnection | undefined => {
  if (!connection || keys.size === 0) return connection

  let tagged = false
  const edges = (connection.edges ?? []).map(edge => {
    const node = edge?.node
    // Only a row that arrived untagged: one the write itself wrote already carries its key.
    if (!node || (node as {[OPTIMISTIC_KEY]?: unknown})[OPTIMISTIC_KEY] != null) return edge
    const key = keys.get(node.id)
    if (key === undefined) return edge
    tagged = true
    return {...edge, node: {...node, [OPTIMISTIC_KEY]: key}}
  })

  return tagged ? {...connection, edges} : connection
}
